import { useEffect, useState } from "react";
import api, { apiError, maskDoc } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, X, User, Stethoscope, Trash2, Clock } from "lucide-react";
import { EmptyState, Modal } from "@/components/panel/ui";
import { Button, Field, Input, PageHeader, Select, Textarea } from "@/components/ui-kit";

const STATUS = {
  agendada: ["Agendada", "bg-slate-100 text-slate-600"],
  confirmada: ["Confirmada", "bg-blue-50 text-blue-700"],
  realizada: ["Realizada", "bg-emerald-50 text-emerald-700"],
  faltou: ["Faltou", "bg-amber-50 text-amber-700"],
  cancelada: ["Cancelada", "bg-red-50 text-red-600"],
};
const FLOW = ["agendada", "confirmada", "realizada", "faltou", "cancelada"];
const iso = (date, time) => new Date(`${date}T${time}`).toISOString();
const fmtTime = (s) => new Date(s).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
const durationMin = (a, b) => Math.max(15, Math.round((new Date(b) - new Date(a)) / 60000));

function SpecialistsModal({ specialists, onClose, onChanged }) {
  const [form, setForm] = useState({ name: "", specialty: "", phone: "" });
  const add = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    try { await api.post("/specialists", form); toast.success("Especialista cadastrado"); setForm({ name: "", specialty: "", phone: "" }); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (s) => { try { await api.delete(`/specialists/${s.id}`); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Modal open onClose={onClose} title="Especialistas" testId="specialists-modal">
      <div className="space-y-2 mb-4">
        {specialists.filter((s) => s.active !== false).map((s) => (
          <div key={s.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
            <span><span className="font-medium text-slate-900">{s.name}</span> <span className="text-xs text-slate-400">{s.specialty}</span></span>
            <button type="button" onClick={() => remove(s)} aria-label={`Remover ${s.name}`}
              className="p-1 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
          </div>
        ))}
        {specialists.length === 0 && <p className="text-sm text-slate-400">Nenhum especialista ainda.</p>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nome" span required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="spec-name" />
        </Field>
        <Field label="Especialidade">
          <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} />
        </Field>
        <Field label="Telefone">
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
      </div>
      <Button onClick={add} block className="mt-3" data-testid="spec-add">Adicionar especialista</Button>
    </Modal>
  );
}

function ApptForm({ appt, day, specialists, onClose, onSaved }) {
  const init = appt
    ? { specialist_id: appt.specialist_id, customer_id: appt.customer_id, customer_name: appt.customer_name, customer_phone: appt.customer_phone || "", date: appt.starts_at.slice(0, 10), time: fmtTime(appt.starts_at), duration: durationMin(appt.starts_at, appt.ends_at), notes: appt.notes || "" }
    : { specialist_id: specialists[0]?.id || "", customer_id: null, customer_name: "", customer_phone: "", date: day, time: "09:00", duration: 30, notes: "" };
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const [custResults, setCustResults] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (form.customer_id || custQuery.trim().length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => api.get("/pos/customers/search", { params: { q: custQuery } }).then((r) => setCustResults(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [custQuery, form.customer_id]);

  const save = async () => {
    if (!form.specialist_id) { toast.error("Selecione o especialista"); return; }
    if (!form.customer_name.trim()) { toast.error("Informe o paciente"); return; }
    setSaving(true);
    try {
      const starts = iso(form.date, form.time);
      const ends = new Date(new Date(`${form.date}T${form.time}`).getTime() + (parseInt(form.duration) || 30) * 60000).toISOString();
      const payload = { specialist_id: form.specialist_id, customer_id: form.customer_id, customer_name: form.customer_name, customer_phone: form.customer_phone, starts_at: starts, ends_at: ends, notes: form.notes };
      if (appt) await api.patch(`/appointments/${appt.id}`, payload);
      else await api.post("/appointments", payload);
      toast.success(appt ? "Agendamento atualizado" : "Consulta agendada");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (status) => {
    try { await api.post(`/appointments/${appt.id}/status`, { status }); toast.success("Status atualizado"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal open onClose={onClose} title={appt ? "Editar consulta" : "Nova consulta"} maxWidth="max-w-md" testId="appt-form">
      <div className="space-y-3">
        {appt && (
          <div className="flex flex-wrap gap-1.5">
            {FLOW.map((s) => (
              <button key={s} onClick={() => changeStatus(s)} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${appt.status === s ? STATUS[s][1] + " ring-2 ring-offset-1 ring-brand-300" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{STATUS[s][0]}</button>
            ))}
          </div>
        )}

        <Field label="Especialista" required>
          <Select value={form.specialist_id} onChange={set("specialist_id")} data-testid="appt-specialist">
            <option value="">Selecione...</option>
            {specialists.filter((s) => s.active !== false).map((s) => <option key={s.id} value={s.id}>{s.name}{s.specialty ? ` — ${s.specialty}` : ""}</option>)}
          </Select>
        </Field>

        <Field label="Paciente" required className="relative">
          {form.customer_id ? (
            <div className="flex items-center justify-between bg-brand-50 border border-brand-200 rounded-lg px-3 py-2 text-sm">
              <span className="flex items-center gap-2 text-brand-800"><User size={14} aria-hidden /> {form.customer_name}</span>
              <button type="button" onClick={() => setForm({ ...form, customer_id: null })} aria-label="Desvincular paciente"
                className="p-1 rounded-md text-brand-500 hover:text-brand-700 hover:bg-brand-100"><X size={14} /></button>
            </div>
          ) : (
            <>
              <Input value={form.customer_name} onChange={(e) => { set("customer_name")(e); setCustQuery(e.target.value); }}
                placeholder="Nome (ou busque cadastrado)" data-testid="appt-customer"
                role="combobox" aria-expanded={custResults.length > 0} aria-autocomplete="list" />
              {custResults.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                  {custResults.map((c) => (
                    <button type="button" key={c.id} onClick={() => { setForm({ ...form, customer_id: c.id, customer_name: c.name, customer_phone: c.phone || form.customer_phone }); setCustResults([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
                      {c.name} <span className="text-xs text-slate-400">{c.document ? maskDoc(c.document) : c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </Field>

        <div className="grid grid-cols-3 gap-2">
          <Field label="Data" required>
            <Input type="date" value={form.date} onChange={set("date")} data-testid="appt-date" className="px-2" />
          </Field>
          <Field label="Hora" required>
            <Input type="time" value={form.time} onChange={set("time")} data-testid="appt-time" className="px-2" />
          </Field>
          <Field label="Duração">
            <Select value={form.duration} onChange={set("duration")} className="px-2">
              {[15, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{m} min</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Telefone"><Input value={form.customer_phone} onChange={set("customer_phone")} /></Field>
        <Field label="Observações"><Textarea value={form.notes} onChange={set("notes")} rows={2} /></Field>

        <button onClick={save} disabled={saving} data-testid="appt-save" className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white font-semibold py-2.5 rounded-lg">{saving ? "Salvando..." : appt ? "Salvar" : "Agendar"}</button>
      </div>
    </Modal>
  );
}

export default function Agenda() {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [specialists, setSpecialists] = useState([]);
  const [filterSpec, setFilterSpec] = useState("");
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSpecs, setShowSpecs] = useState(false);

  const loadSpecs = () => api.get("/specialists").then((r) => setSpecialists(r.data)).catch(() => {});
  const load = () => api.get("/appointments", { params: { from: day, to: day, ...(filterSpec ? { specialist_id: filterSpec } : {}) } }).then((r) => setItems(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSpecs(); }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, filterSpec]);

  const shiftDay = (n) => { const d = new Date(day + "T12:00:00"); d.setDate(d.getDate() + n); setDay(d.toISOString().slice(0, 10)); };
  // `capitalize` do CSS maiusculiza TODA palavra: "sábado, 01 de agosto" saía
  // como "Sábado, 01 De Agosto". Só a primeira letra é o que se quer.
  const dayLabel = new Date(day + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
  const dayLabelUp = dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1);

  return (
    <div className="space-y-5" data-testid="admin-agenda-page">
      <PageHeader
        eyebrow="Operação"
        title="Agenda de consultas"
        icon={CalendarDays}
        description="Consultas com os especialistas da loja, dia a dia. Toque numa consulta para editar ou mudar o status."
        actions={(
          <>
            <Button variant="secondary" icon={Stethoscope} onClick={() => setShowSpecs(true)} data-testid="agenda-specialists-btn">
              Especialistas
            </Button>
            <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }} data-testid="agenda-new-btn">
              Nova consulta
            </Button>
          </>
        )}
      />

      {/* A barra de navegação do dia estourava a tela do celular em 30px: o
          rótulo por extenso, o campo de data e o filtro disputavam UMA linha que
          não quebrava. Agora a data ganha a própria linha abaixo de `sm`, e o
          rótulo some — quem está no celular já lê a data no campo. */}
      <div className="bg-white border border-slate-200 rounded-xl px-3 sm:px-4 py-3 space-y-3 sm:space-y-0 sm:flex sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <button type="button" onClick={() => shiftDay(-1)} aria-label="Dia anterior" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"><ChevronLeft size={18} /></button>
          <button type="button" onClick={() => setDay(new Date().toISOString().slice(0, 10))} className="text-xs font-semibold text-brand-600 px-2 py-2 shrink-0">Hoje</button>
          <button type="button" onClick={() => shiftDay(1)} aria-label="Dia seguinte" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"><ChevronRight size={18} /></button>
          <span className="hidden lg:inline text-sm font-semibold text-slate-900 ml-2 truncate">{dayLabelUp}</span>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="agenda-date"
            aria-label="Dia exibido na agenda" className="w-auto min-w-0 px-2 ml-1 sm:ml-2" />
        </div>
        {/* Fora do desktop o dia por extenso vira uma linha própria: é a
            informação que responde "que dia estou vendo?" sem ler o campo. */}
        <p className="lg:hidden text-sm font-semibold text-slate-900">{dayLabelUp}</p>
        <Select value={filterSpec} onChange={(e) => setFilterSpec(e.target.value)} data-testid="agenda-filter-spec"
          aria-label="Filtrar por especialista" className="w-full sm:w-auto px-2.5 text-slate-600">
          <option value="">Todos os especialistas</option>
          {specialists.filter((s) => s.active !== false).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl">
        {items.length === 0 ? (
          <EmptyState icon={CalendarDays} title="Nenhuma consulta neste dia" desc="Clique em 'Nova consulta' para agendar." testId="agenda-empty" />
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((a) => (
              <button key={a.id} onClick={() => { setEditing(a); setShowForm(true); }} data-testid="agenda-appt-row"
                className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-slate-50">
                <div className="flex items-center gap-1.5 text-slate-900 font-mono font-semibold w-32 shrink-0"><Clock size={14} className="text-slate-400" /> {fmtTime(a.starts_at)}–{fmtTime(a.ends_at)}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{a.customer_name}</p>
                  <p className="text-xs text-slate-400">{a.specialist?.name}{a.specialist?.specialty ? ` · ${a.specialist.specialty}` : ""}{a.customer_phone ? ` · ${a.customer_phone}` : ""}</p>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${STATUS[a.status]?.[1]}`}>{STATUS[a.status]?.[0]}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {showForm && <ApptForm appt={editing} day={day} specialists={specialists} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {showSpecs && <SpecialistsModal specialists={specialists} onClose={() => setShowSpecs(false)} onChanged={loadSpecs} />}
    </div>
  );
}
